// { "YYYY-MM-DD": "Holiday name" } for the detected country — see
// src/utils/holidays.js. Empty until loaded, and when the country has none.
import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { detectCountry } from "../../firebase/billing";
import { apiUrl } from "../../firebase/apiUrl";
import {
  currentHolidayCountry, countryCheckDue, rememberHolidayCountry,
  loadHolidays, loadedHolidays, refreshHolidays, HOLIDAY_CHOICE_EVENT,
} from "../../utils/holidays";

const EMPTY = {};
// Once per app run at most, even when the answer never comes (offline, or
// no country header), so re-opening the Planner doesn't re-ask each time.
let askedThisRun = false;

export function useHolidays() {
  const [country, setCountry] = useState(currentHolidayCountry);
  const [holidays, setHolidays] = useState(() => loadedHolidays(country) || EMPTY);

  // Ask the server where we are, at most once a day. Offline / no answer
  // keeps whatever we had.
  useEffect(() => {
    if (askedThisRun || !countryCheckDue()) return undefined;
    askedThisRun = true;
    let alive = true;
    detectCountry().then(cc => {
      // Remembered even while a country is picked by hand, so switching back
      // to Regional is right at once. What shows still follows the choice.
      if (rememberHolidayCountry(cc) && alive) setCountry(currentHolidayCountry());
    });
    return () => { alive = false; };
  }, []);

  // Settings → Planner Options → Holidays changed.
  useEffect(() => {
    const onChoice = () => setCountry(currentHolidayCountry());
    window.addEventListener(HOLIDAY_CHOICE_EVENT, onChoice);
    return () => window.removeEventListener(HOLIDAY_CHOICE_EVENT, onChoice);
  }, []);

  useEffect(() => {
    if (!country) { setHolidays(EMPTY); return undefined; }
    let alive = true;
    const ready = loadedHolidays(country);
    if (ready) setHolidays(ready);
    const refresh = () => {
      // Android only: the web app's own copy already is the website's.
      if (!Capacitor.isNativePlatform()) return;
      refreshHolidays(country, apiUrl(`/holidays/${country}.json`))
        .then(map => { if (alive && map) setHolidays(map); });
    };
    if (ready) { refresh(); return () => { alive = false; }; }
    loadHolidays(country).then(map => { if (alive) setHolidays(map); refresh(); });
    return () => { alive = false; };
  }, [country]);

  return holidays;
}
